import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Check } from "lucide-react";
import { activeCourses } from "@/data/site";
import { useBooking } from "@/components/site/BookingContext";

const CourseCard = ({ course, index }) => {
  const { openBooking } = useBooking();
  return (
    <motion.div
      data-testid={`course-card-${course.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative flex flex-col border border-border bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-20px_rgba(0,0,0,0.25)] ${
        course.featured ? "lg:row-span-2 ring-1 ring-brand-red" : ""
      }`}
    >
      <div className="relative overflow-hidden aspect-[16/10]">
        <Link to={`/corsi/${course.id}`} data-testid={`course-image-link-${course.id}`}>
          <img
            src={course.image}
            alt={course.title}
            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
          />
        </Link>
        <div className="absolute top-4 left-4 bg-brand-red text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1.5">
          {course.tag}
        </div>
      </div>

      <div className="flex flex-col flex-1 p-7">
        <div className="text-[11px] uppercase tracking-[0.2em] text-brand-ink mb-2">
          {course.level}
        </div>
        <Link to={`/corsi/${course.id}`} data-testid={`course-title-link-${course.id}`}>
          <h3 className="font-heading font-bold text-2xl md:text-3xl tracking-tight text-brand-black leading-none hover:text-brand-red transition-colors">
            {course.title}
          </h3>
        </Link>
        <div className="text-sm text-brand-ink mt-1">{course.subtitle}</div>

        <p className="text-sm text-brand-ink leading-relaxed mt-4">
          {course.description}
        </p>

        <ul className="mt-5 space-y-2">
          {course.points.map((p) => (
            <li key={p} className="flex items-start gap-2 text-sm text-brand-black">
              <Check size={16} className="text-brand-red mt-0.5 shrink-0" />
              {p}
            </li>
          ))}
        </ul>

        <Link
          to={`/corsi/${course.id}`}
          data-testid={`course-details-link-${course.id}`}
          className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-brand-red hover-underline w-fit"
        >
          Programma e date <ArrowUpRight size={14} />
        </Link>

        <div className="mt-auto pt-7 flex items-end justify-between gap-4">
          <div className="flex items-center gap-1.5 text-xs text-brand-ink uppercase tracking-widest">
            <Clock size={13} /> {course.duration}
          </div>
          <button
            data-testid={`course-cta-${course.id}`}
            onClick={() => openBooking(course.title)}
            className="inline-flex items-center gap-1.5 bg-brand-black text-white text-xs font-bold uppercase tracking-widest px-5 py-3 hover:bg-brand-red transition-colors"
          >
            Prenota
            <ArrowUpRight size={15} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export const Courses = ({ showHeader = true }) => {
  return (
    <section id="corsi" data-testid="courses-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        {showHeader && (
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-red mb-4">
              I nostri corsi
            </div>
            <h2 className="font-heading font-bold text-4xl md:text-6xl tracking-tighter text-brand-black leading-none">
              Scegli il tuo   percorso
            </h2>
          </div>
          <p className="max-w-sm text-sm text-brand-ink leading-relaxed">
            Corsi di formazione e aggiornamento per privati, aziende e professionisti, con percorsi dedicati alle diverse esigenze.
          </p>
        </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {activeCourses().map((c, i) => (
            <CourseCard key={c.id} course={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
